(function () {
  'use strict';

  var PERSONAL_DOMAINS = ['gmail', 'yahoo', 'hotmail', 'outlook', 'icloud', 'aol', 'live'];

  // ── React-controlled input setter ─────────────────────────
  // Klaviyo uses React, so direct .value = x doesn't trigger state.
  // The native setter bypasses React's override, then the dispatched
  // events propagate through React's SyntheticEvent system.
  function setReactValue(input, value) {
    var setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    setter.call(input, value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function isPersonalDomain(email) {
    var parts = email.split('@');
    if (parts.length < 2) return false;
    var base = parts[1].split('.')[0].toLowerCase();
    return PERSONAL_DOMAINS.indexOf(base) !== -1;
  }

  // ── Klaviyo form locator ───────────────────────────────────
  function waitForKlaviyoForm(sinkEl, timeoutMs) {
    return new Promise(function (resolve) {
      var deadline = Date.now() + (timeoutMs || 5000);
      function check() {
        var form = sinkEl.querySelector('form[data-testid^="klaviyo-form"]')
                || sinkEl.querySelector('form.klaviyo-form');
        if (form) { resolve(form); return; }
        if (Date.now() >= deadline) { resolve(null); return; }
        setTimeout(check, 200);
      }
      check();
    });
  }

  // ── Fill Klaviyo inputs ────────────────────────────────────
  // Radio values in the custom form are set to exactly match Klaviyo's
  // aria-label attributes, so we can target radios directly by value.
  function fillKlaviyoForm(kForm, data) {
    var textFields = [
      { sel: 'input[autocomplete="given-name"], input[id^="first_name_"]', val: data.first_name },
      { sel: 'input[autocomplete="family-name"], input[id^="last_name_"]', val: data.last_name },
      { sel: 'input[id^="CompanyName_"]',                                  val: data.company },
      { sel: 'input[type="email"]',                                        val: data.email },
    ];

    textFields.forEach(function (f) {
      var el = kForm.querySelector(f.sel);
      if (el && f.val) setReactValue(el, f.val);
    });

    var teamRadio   = kForm.querySelector('input[type="radio"][aria-label="' + data.team_size + '"]');
    var budgetRadio = kForm.querySelector('input[type="radio"][aria-label="' + data.budget + '"]');
    if (teamRadio)   teamRadio.click();
    if (budgetRadio) budgetRadio.click();
  }

  function submitKlaviyoForm(kForm) {
    return new Promise(function (resolve) {
      requestAnimationFrame(function () {
        var btn = kForm.querySelector('.klaviyo-form-button')
               || kForm.querySelector('[data-action-id]')
               || kForm.querySelector('button[type="button"]');
        if (btn) { btn.click(); resolve(true); }
        else     { resolve(false); }
      });
    });
  }

  // ── Watch for Klaviyo success DOM change ──────────────────
  function watchForKlaviyoSuccess(sinkEl, cb, timeoutMs) {
    var fired = false;
    function fire() {
      if (fired) return;
      fired = true;
      observer.disconnect();
      cb();
    }

    var observer = new MutationObserver(function () {
      var success = sinkEl.querySelector('[data-testid*="success"]')
                 || sinkEl.querySelector('[class*="success"]');
      var formGone = !sinkEl.querySelector('form[data-testid^="klaviyo-form"]');
      if (success || formGone) fire();
    });

    observer.observe(sinkEl, { childList: true, subtree: true, attributes: true });
    setTimeout(fire, timeoutMs || 4000);
  }

  // ── Validation UI helpers ──────────────────────────────────
  function showInputError(inputEl, hintEl, msg) {
    inputEl.setAttribute('aria-invalid', 'true');
    if (hintEl) {
      hintEl.textContent = msg;
      hintEl.classList.add('ma-lf__field-hint--error');
      hintEl.removeAttribute('hidden');
    }
  }

  function clearInputError(inputEl, hintEl) {
    inputEl.removeAttribute('aria-invalid');
    if (hintEl) {
      hintEl.textContent = '';
      hintEl.classList.remove('ma-lf__field-hint--error');
      hintEl.setAttribute('hidden', '');
    }
  }

  function showGroupError(fieldsetEl, msg) {
    fieldsetEl.classList.add('ma-lf__field-group--error');
    var hint = fieldsetEl.querySelector('.ma-lf__pills-hint');
    if (!hint) {
      hint = document.createElement('p');
      hint.className = 'ma-lf__field-hint ma-lf__field-hint--error ma-lf__pills-hint';
      fieldsetEl.appendChild(hint);
    }
    hint.textContent = msg;
  }

  function clearGroupError(fieldsetEl) {
    fieldsetEl.classList.remove('ma-lf__field-group--error');
    var hint = fieldsetEl.querySelector('.ma-lf__pills-hint');
    if (hint) hint.remove();
  }

  // ── Init ──────────────────────────────────────────────────
  function initLeadForm(customForm) {
    var section   = customForm.closest('.ma-lf') || customForm.parentElement;
    var sinkEl    = section.querySelector('.ma-lf__klaviyo-sink');
    var statusEl  = customForm.querySelector('.ma-lf__status');
    var submitBtn = customForm.querySelector('.ma-lf__submit');
    var submitted = false;

    customForm.addEventListener('submit', async function (e) {
      e.preventDefault();
      if (submitted) return;

      var firstInput   = customForm.querySelector('input[name="ma_first_name"]');
      var lastInput    = customForm.querySelector('input[name="ma_last_name"]');
      var companyInput = customForm.querySelector('input[name="ma_company"]');
      var emailInput   = customForm.querySelector('input[name="ma_email"]');
      var emailHint    = emailInput.closest('.ma-lf__field-group')
                           ?.querySelector('.ma-lf__field-hint') || null;
      var teamFieldset   = customForm.querySelector('[data-field="team_size"]');
      var budgetFieldset = customForm.querySelector('[data-field="budget"]');
      var teamValue   = customForm.querySelector('input[name="ma_team_size"]:checked')?.value || '';
      var budgetValue = customForm.querySelector('input[name="ma_budget"]:checked')?.value || '';

      // Validate
      var hasErrors = false;

      [firstInput, companyInput].forEach(function (inp) {
        if (inp && !inp.value.trim()) {
          inp.setAttribute('aria-invalid', 'true');
          hasErrors = true;
        } else if (inp) {
          inp.removeAttribute('aria-invalid');
        }
      });

      clearInputError(emailInput, emailHint);
      if (!emailInput.value.trim()) {
        showInputError(emailInput, emailHint, 'Work email is required.');
        hasErrors = true;
      } else if (isPersonalDomain(emailInput.value)) {
        showInputError(emailInput, emailHint, "Use your work email — we don't quote to personal domains.");
        hasErrors = true;
      }

      if (!teamValue)   { showGroupError(teamFieldset, 'Please select a team size.');   hasErrors = true; }
      else              { clearGroupError(teamFieldset); }
      if (!budgetValue) { showGroupError(budgetFieldset, 'Please select a budget.');    hasErrors = true; }
      else              { clearGroupError(budgetFieldset); }

      if (hasErrors) return;

      // Lock UI
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
      statusEl.removeAttribute('hidden');

      var data = {
        first_name: firstInput.value.trim(),
        last_name:  lastInput ? lastInput.value.trim() : '',
        company:    companyInput.value.trim(),
        email:      emailInput.value.trim(),
        team_size:  teamValue,
        budget:     budgetValue,
      };

      function showSuccess() {
        if (submitted) return;
        submitted = true;
        customForm.dataset.state = 'success';
        statusEl.dataset.status = 'success';
        statusEl.textContent = "You're all set — a rep will be in touch within 1 business day.";
        statusEl.removeAttribute('hidden');
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
      }

      function showError() {
        statusEl.dataset.status = 'error';
        statusEl.textContent = 'Something went wrong. Please try again or email us directly.';
        statusEl.removeAttribute('hidden');
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
      }

      // Fallback: Klaviyo JS API (if embed not found)
      function submitViaApi() {
        var payload = {
          '$first_name': data.first_name,
          '$last_name':  data.last_name,
          '$email':      data.email,
          'Company':     data.company,
          'Team Members': data.team_size,
          'Budget Per Employee': data.budget,
        };
        try {
          if (window.klaviyo && window.klaviyo.identify) {
            window.klaviyo.identify(payload);
          } else if (window._learnq) {
            window._learnq.push(['identify', payload]);
          }
          showSuccess();
        } catch (_) {
          showError();
        }
      }

      if (!sinkEl) { submitViaApi(); return; }

      var kForm = await waitForKlaviyoForm(sinkEl, 5000);
      if (!kForm) { submitViaApi(); return; }

      fillKlaviyoForm(kForm, data);
      await submitKlaviyoForm(kForm);
      watchForKlaviyoSuccess(sinkEl, showSuccess, 4000);
    });
  }

  function boot() {
    document.querySelectorAll('.ma-lf__custom-form').forEach(initLeadForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
